/**
 * Apple Sign In SDK Utilities
 *
 * Handles loading, initialization, and sign-in flow for Apple Sign In JS SDK.
 *
 * @see https://developer.apple.com/documentation/signinwithapplejs
 */

// Import type declarations
import type { AppleSignInResponse } from '@/types/apple.d';

/** Apple Sign In JS SDK URL */
const APPLE_SDK_URL = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

/** Promise for SDK loading (singleton to prevent multiple loads) */
let sdkLoadPromise: Promise<void> | null = null;

/**
 * Load the Apple Sign In JS SDK
 *
 * Returns a promise that resolves when the SDK is loaded.
 * Multiple calls return the same promise (idempotent).
 *
 * @returns Promise that resolves when SDK is ready
 * @throws Error if SDK fails to load
 */
export function loadAppleAuthSDK(): Promise<void> {
  // Return existing promise if already loading/loaded
  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.AppleID) {
      resolve();
      return;
    }

    // Create and inject script element
    const script = document.createElement('script');
    script.src = APPLE_SDK_URL;
    script.async = true;

    script.onload = () => {
      // Verify SDK is available
      if (window.AppleID) {
        resolve();
      } else {
        reject(new Error('Apple Sign In SDK loaded but AppleID not available'));
      }
    };

    script.onerror = () => {
      // Reset promise so retry is possible
      sdkLoadPromise = null;
      reject(new Error('Failed to load Apple Sign In SDK'));
    };

    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}

/**
 * Initialize Apple Sign In SDK with configuration
 *
 * Must be called after loadAppleAuthSDK() resolves.
 *
 * @param clientId - Apple Services ID
 * @param redirectUri - Redirect URI (for popup mode, typically current origin)
 * @param usePopup - Whether to use popup mode (default: true)
 * @throws Error if SDK is not loaded
 */
export function initAppleAuth(
  clientId: string,
  redirectUri: string,
  usePopup: boolean = true
): void {
  if (!window.AppleID) {
    throw new Error('Apple Sign In SDK not loaded');
  }

  window.AppleID.auth.init({
    clientId,
    scope: 'email name',
    redirectURI: redirectUri,
    usePopup,
    // Generate unique state for CSRF protection
    state: crypto.randomUUID(),
  });
}

/**
 * Trigger Apple Sign In flow
 *
 * Opens Apple's authentication popup/redirect and returns the response.
 * Must be called after initAppleAuth().
 *
 * @returns Promise with Apple Sign In response containing id_token and optional user info
 * @throws Error if SDK is not loaded or user cancels
 */
export async function signInWithApple(): Promise<AppleSignInResponse> {
  if (!window.AppleID) {
    throw new Error('Apple Sign In SDK not loaded');
  }

  return window.AppleID.auth.signIn();
}

/**
 * Check if Apple Sign In SDK is loaded and ready
 *
 * @returns true if SDK is available
 */
export function isAppleSdkReady(): boolean {
  return Boolean(window.AppleID);
}
